import { useEffect, useState } from "react";
import ProductsList from "../../components/Product/ProductsList";
import ProductPageHeader from "../../components/ProductPageHeader";
import { Product } from "../../types/ProductType";
import { Branch, Category, Chef } from "../../types/globalTypes";
import { useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";

import ErrorMessage from "../../components/ErrorMessage";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";

function Products() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [products, setProducts] = useState<Product[]>([]);
  const [AllCategories, setAllCategories] = useState<Category[]>([]);
  const [AllBranches, setAllBranches] = useState<Branch[]>([]);
  const [chefs, setChefs] = useState<Chef[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needRefetch, setNeedRefetch] = useState(false);

  const BASE_URL = import.meta.env.VITE_BASE_URL;
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token
  );

  const productsUrl = `${BASE_URL}/api/dashboard/products?locale=${lang}`;
  const categoriesUrl = `${BASE_URL}/api/dashboard/categories?locale=${lang}`;
  const branchesUrl = `${BASE_URL}/api/dashboard/branches?locale=${lang}`;
  const chefsUrl = `${BASE_URL}/api/dashboard/chefs?locale=${lang}`;

  useEffect(() => {
    if (!token) return;

    setIsLoading(true);
    setError(null);
    setNeedRefetch(false);

    async function fetchCategories() {
      try {
        const res = await axios.get(categoriesUrl, {
          headers: { Authorization: token },
        });

        if (res.data.success === false) {
          setError(res.data.error);
          toast.error(res.data.error);
          return;
        }

        return res.data?.categories || [];
      } catch (error: unknown) {
        const message = axios.isAxiosError(error)
          ? error.response?.data?.error || t("products.categoriesFetchError")
          : t("products.unexpectedError");

        setError(message as string);
        toast.error(message as string);
      }
    }

    async function fetchProducts() {
      try {
        const res = await axios.get(productsUrl, {
          headers: { Authorization: token },
        });
        return res.data?.products || [];
      } catch (error: unknown) {
        const message = axios.isAxiosError(error)
          ? error.response?.data?.error || t("products.fetchError")
          : t("products.unexpectedError");

        setError(message as string);
        toast.error(message as string);
      }
    }

    async function fetchBranches() {
      try {
        const res = await axios.get(branchesUrl, {
          headers: { Authorization: token },
        });
        return res.data?.branches || [];
      } catch (error: unknown) {
        const message = axios.isAxiosError(error)
          ? error.response?.data?.error || t("products.branchesFetchError")
          : t("products.unexpectedError");

        setError(message as string);
        toast.error(message as string);
      }
    }

    async function fetchChefs() {
      try {
        const res = await axios.get(chefsUrl, {
          headers: { Authorization: token },
        });
        return res.data?.users || [];
      } catch (error: unknown) {
        const message = axios.isAxiosError(error)
          ? error.response?.data?.error || t("products.chefsFetchError")
          : t("products.unexpectedError");

        setError(message as string);
        toast.error(message as string);
      }
    }

    Promise.all([fetchCategories(), fetchProducts(), fetchBranches(), fetchChefs()])
      .then(([categories, products, branches, chefs]) => {
        setAllCategories(categories);
        setProducts(products);
        setAllBranches(branches);
        setChefs(chefs);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        const message = t("products.unexpectedError");
        setError(message as string);
        toast.error(message as string);
      })
      .finally(() => setIsLoading(false));
  }, [branchesUrl, categoriesUrl, needRefetch, productsUrl, token, t, chefsUrl]);

  if (isLoading)
    return (
      <div className="mt-[9rem] grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        <Skeleton className="dark:bg-slate-600 dark:bg-opacity-35 h-[500px] md:h-[550px]" />
        <Skeleton className="dark:bg-slate-600 dark:bg-opacity-35 h-[500px] md:h-[550px]" />
        <Skeleton className="dark:bg-slate-600 dark:bg-opacity-35 h-[500px] md:h-[550px]" />
        <Skeleton className="dark:bg-slate-600 dark:bg-opacity-35 h-[500px] md:h-[550px]" />
      </div>
    );

  if (error) return <ErrorMessage msg={error} />;

  return (
    <div className="flex flex-col gap-7">
      <ProductPageHeader
        allBranches={AllBranches}
        allCategories={AllCategories}
        allChefs={chefs}
        setNeedRefetch={setNeedRefetch}
        productsLength={products.length}
      />
      <ProductsList
        products={products}
        allBranches={AllBranches}
        allCategories={AllCategories}
        setNeedRefetch={setNeedRefetch}
      />
    </div>
  );
}

export default Products;
